import shortUUID from "short-uuid";
import type {
  IDocumentData,
  IFormImageVideoNode,
  IFrameNode,
  IImageNode,
  IVideoNode,
} from "../node/type";
import { findAllNodeByPredicate } from "../node/util";
import type { SyncType, VendorType } from "../schema/store";
import type { PageInfoMeta } from "../types/campaign";
import type {
  Data,
  DataSource,
  ICampaignListData,
  ICampaignListDataItem,
  IChildLayoutsData,
  IDataMap,
  IHmallLiveBroadProductListData,
  IInstagramDataSourceV2,
  IInstagramDataV2,
  ILocalData,
  IPartialCampaignListData,
  IPartialCampaignListDataItem,
  IPopularProductListData,
  ITimeData,
  IVideoData,
  IVideoDataSourceParam,
  PartialChildLayoutsData,
  PartialData,
  PartialHmallLiveBroadProductListData,
  PartialInsagramDataV2,
  PartialLocalData,
  PartialPopularProductListData,
  PartialTimeData,
  PartialVideoData,
  VideoInfo,
} from "../types/data-source";
import { isObject } from "./value";

type GetDataSourceValueArgs = {
  dataMap: IDataMap;
  keyString: string;
};

export const getDataSourceValue = ({
  dataMap,
  keyString,
}: GetDataSourceValueArgs) => {
  try {
    const [dataSourceName, ...keys] = keyString.split(".");

    if (!dataSourceName) {
      throw new Error("incorrect template syntax");
    }

    const dataSource = dataMap[dataSourceName]?.data;

    // dataSource가 없을 경우 null 반환(프레임과 이미지에서 비디오 데이터를 찾기 때문에, Error 반환시 warning 콘솔이 발생이 이유)
    if (!dataSource) {
      return null;
    }

    return getNestedObjectValue(dataSource, keys);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(err);
  }
};

export const isKeyOfObject = (obj: object, key: string) =>
  Object.keys(obj).includes(key);

export const getNestedObjectValue = (object: unknown, keys: string[]) => {
  let nestedValue: unknown = object;

  for (const key of keys) {
    if (!isObject(nestedValue) || !isKeyOfObject(nestedValue, key)) {
      throw new Error("invalid key in object");
    }

    nestedValue = nestedValue[key as keyof typeof nestedValue];
  }

  return nestedValue;
};

export const createData = (initialData?: PartialData) => {
  switch (initialData?.name) {
    case "instagram":
      return createInstagramDataV2(initialData);
    case "local":
      return createLocalData(initialData);
    case "video":
      return createVideoData(initialData);
    case "time":
      return createTimeData(initialData);
    case "campaign_list":
      return createCampaignListData(initialData);
    case "child_layouts":
      return createChildLayoutsData(initialData);
    case "hmall_live_broad_product_list":
      return createHmallLiveBroadProductListData(initialData);
    case "popular_product_list":
      return createPopularProductListData(initialData);
    default:
      return createLocalData();
  }
};

export const createVideoData = (initialData?: PartialVideoData): IVideoData => {
  return {
    id: initialData?.id ?? shortUUID.generate(),
    name: "video",
    data: initialData?.data ?? [],
  };
};

export const createTimeData = (initialData?: PartialTimeData): ITimeData => {
  return {
    id: initialData?.id ?? shortUUID.generate(),
    name: "time",
    data: initialData?.data ?? {},
  };
};

export const createCampaignListData = (
  initialData?: IPartialCampaignListData
): ICampaignListData => {
  initialData?.data;
  return {
    id: initialData?.id ?? shortUUID.generate(),
    name: "campaign_list",
    data:
      initialData?.data && "campaigns" in initialData.data
        ? {
            campaigns: initialData?.data.campaigns.map(
              createCampaignListDataItem
            ),
          }
        : { campaigns: [] },
  };
};

export const createCampaignListDataItem = (
  item?: IPartialCampaignListDataItem
): ICampaignListDataItem => {
  return {
    content: item?.content ?? {},
    order: item?.order ?? 0,
    publicId: item?.publicId ?? shortUUID.generate(),
    schedule: item?.schedule ?? {
      type: "DATETIME_RANGE",
      start: new Date().toISOString(),
      end: null,
    },
  };
};

export const createChildLayoutsData = (
  initialData?: PartialChildLayoutsData
): IChildLayoutsData => {
  return {
    id: initialData?.id ?? shortUUID.generate(),
    name: "child_layouts",
    data: initialData?.data ?? {
      mobileChildLayout: {},
      desktopChildLayout: {},
    },
  };
};

export const createHmallLiveBroadProductListData = (
  initialData?: PartialHmallLiveBroadProductListData
): IHmallLiveBroadProductListData => {
  return {
    id: initialData?.id ?? shortUUID.generate(),
    name: "hmall_live_broad_product_list",
    data: initialData?.data ?? { items: [] },
  };
};

export const createPopularProductListData = (
  initialData?: PartialPopularProductListData
): IPopularProductListData => {
  return {
    id: initialData?.id ?? shortUUID.generate(),
    name: "popular_product_list",
    data: initialData?.data ?? { items: [] },
  };
};

export const createLocalData = (initialData?: PartialLocalData): ILocalData => {
  return {
    id: initialData?.id ?? shortUUID.generate(),
    name: "local",
    data: initialData?.data ?? {},
  };
};

export const createInstagramDataV2 = (
  initialData?: PartialInsagramDataV2
): IInstagramDataV2 => {
  return {
    id: initialData?.id ?? shortUUID.generate(),
    name: "instagram",
    data: initialData?.data ?? { username: "", posts: [] },
  };
};

export const createDataMap = (initialDataMap?: {
  [name: string]: PartialData;
}): IDataMap => {
  const localData = initialDataMap?.local as PartialLocalData | undefined;

  return {
    local: createLocalData(localData),
    ...Object.entries(initialDataMap ?? {}).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: createData(value),
      }),
      {}
    ),
  };
};

export const getData = <T extends Data = ILocalData>(
  dataMap: IDataMap,
  key: string
) => {
  return dataMap[key] as T | undefined;
};

export const getDataSummary = (data: Data) => {
  switch (data.name) {
    case "instagram":
      return {
        name: data.name,
        count: data.data.posts.length,
        publicIds: data.data.posts.map((post) => post.publicId),
        username: data.data.username,
      };
    case "single_product":
      return {
        name: data.name,
        productId: data.data.id,
        productCode: data.data.product_code,
        updatedAt: data.data.updated_at,
      };

    case "product_list":
    case "hmall_live_broad_product_list":
    case "popular_product_list":
      return {
        name: data.name,
        count: data.data.items.length,
      };

    case "local":
      return {
        name: data.name,
        data: data.data,
      };

    case "user":
      return {
        name: data.name,
        data: data.data,
      };

    case "data":
      return {
        name: data.name,
        data: data.data,
      };

    case "video":
      return {
        name: data.name,
        count: data.data.length,
        filenames: data.data.map((video) => video.filename),
      };

    case "time":
      return {
        name: data.name,
        data: data.data,
      };

    case "child_layouts":
      return {
        name: data.name,
        data: data.data,
      };

    case "campaign_list":
    case "social_proof":
      return {
        name: data.name,
        data: data.data,
      };
  }
};

export const getDataSourceBySourceType = <T extends DataSource>(
  dataSources: DataSource[],
  sourceType: T["sourceType"]
): T | undefined => {
  const dataSourceFromSourceType = dataSources.find(
    (dataSource): dataSource is T => dataSource.sourceType === sourceType
  );

  return dataSourceFromSourceType;
};

export const getDataSourcesBySourceType = <T extends DataSource>(
  dataSources: DataSource[],
  sourceType: T["sourceType"]
): T[] | [] => {
  const dataSourcesFromSourceType = dataSources.filter(
    (dataSource): dataSource is T => dataSource.sourceType === sourceType
  );

  return dataSourcesFromSourceType;
};

export const getInstagramSourceType = (dataSource: IInstagramDataSourceV2) => {
  return dataSource.sourceParam.source.type;
};

export const mergeVideoDataMaps = (
  dataMaps: PartialVideoData[]
): IVideoData => {
  const mergedData: VideoInfo[] = [];

  const id = dataMaps.find((dataMap) => dataMap?.id)?.id ?? "";

  dataMaps.forEach((dataMap) => {
    if (!dataMap || !dataMap.data) {
      return;
    }

    dataMap.data.forEach((video) => {
      const existingIndex = mergedData.findIndex(
        (item) => item.filename === video.filename
      );
      if (existingIndex !== -1) {
        mergedData[existingIndex] = video; // 최신 데이터로 교체
      } else {
        mergedData.push(video);
      }
    });
  });

  return {
    id, // id는 임의로 첫 번째 dataMap의 id를 사용
    name: "video",
    data: mergedData,
  };
};

/**
 * 역할)
 * 1. Temp Campaign 혹은 Template DocumentData를 인자로 받았을 때, VideoDataSource를 세팅합니다.
 * 2. VideoDataSource를 Upsert 하기전에, DocumentData를 기반으로 VideoDataSource를 세팅합니다.
 */
export const convertDocumentDataToVideoSourceParam = (
  documentData: IDocumentData
): IVideoDataSourceParam => {
  const documentNodes = documentData.documents;

  if (!documentNodes || documentNodes.length === 0) {
    return { data: [] };
  }

  const videoNodes = findAllNodeByPredicate(
    documentNodes,
    (
      node
    ): node is IVideoNode | IImageNode | IFrameNode | IFormImageVideoNode =>
      node.type === "VIDEO" ||
      node.type === "IMAGE" ||
      node.type === "FRAME" ||
      node.type === "FORM_IMAGE_VIDEO"
  );

  const sourceParam: IVideoDataSourceParam["data"] = [];
  videoNodes.forEach((videoNode) => {
    if (videoNode.type === "IMAGE" || videoNode.type === "VIDEO") {
      if (!videoNode || !videoNode.videoInfo || !videoNode.videoInfo.fileName) {
        return;
      }

      const sourceParamData = {
        nodeId: videoNode.id,
        filename: videoNode.videoInfo.fileName,
      };

      sourceParam.push(sourceParamData);
    } else if (
      videoNode.type === "FRAME" ||
      videoNode.type === "FORM_IMAGE_VIDEO"
    ) {
      const fill = videoNode.fills.find((fill) => fill.type === "IMAGE");

      if (!videoNode || fill?.type !== "IMAGE" || !fill.video?.fileName) {
        return;
      }

      const sourceParamData = {
        nodeId: videoNode.id,
        filename: fill.video.fileName,
      };
      sourceParam.push(sourceParamData);
    }
  });

  return {
    data: sourceParam,
  };
};

/** @deprecated single product 실제로 사용되지 않음 */
export const getSingleProductDataAlias = ({
  syncType,
  originalStoreId,
  originalSubstoreId,
  vendorType,
  externalId,
}: {
  syncType: SyncType;
  originalStoreId: string;
  originalSubstoreId: string | null;
  vendorType: VendorType;
  externalId: string;
}) => {
  return `${vendorType}:${originalStoreId}:${originalSubstoreId}:${syncType}:${externalId}`;
};

/**
 * @depredated
 *
 * front-app에 데이터맵을 보여줄 때 alias가 정해져있지 않을 경우 필요함.
 * 하지만 장기적으로 에디터에서 직접 api-private으로 데이터 요청을 하고
 * 데이터맵 로직은 에디터에서 사용하지 않는 방향으로 하는 게 좋을 것 같음. 이게 더 단순함
 * 데이터맵 로직은 svelte(component)에서만 사용하도록 함.
 * */
export const getAliasFromData = (_data: Data) => {
  // if (data.name === "single_product") {
  //   return getSingleProductDataAlias({
  //     syncType: SyncType.Product,
  //     originalStoreId: data.data.original_store_id,
  //     vendorType: data.data.vendor_type,
  //     externalId: data.data.external_id,
  //   });
  // }
  return null;
};

export const getCampaignListDataContent = (
  pageInfoMetaArr: PageInfoMeta[]
): ICampaignListDataItem["content"] => {
  return pageInfoMetaArr.reduce((acc, pageInfoMeta) => {
    const { type, key, info } = pageInfoMeta;

    switch (type) {
      case "TEXTAREA": {
        return {
          ...acc,
          [key]: info.value,
        };
      }

      case "TEXT": {
        return {
          ...acc,
          [key]: info.value,
        };
      }

      case "IMAGE": {
        switch (key) {
          case "desktopThumbnail": {
            return {
              ...acc,
              desktopThumbnailUrl: info.path,
              desktopThumbnailWidth: info.width,
              desktopThumbnailHeight: info.height,
            };
          }

          case "mobileThumbnail": {
            return {
              ...acc,
              mobileThumbnailUrl: info.path,
              mobileThumbnailWidth: info.width,
              mobileThumbnailHeight: info.height,
            };
          }
        }
      }
    }
  }, {});
};
